import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class RecurringEpService {

  constructor(private http: HttpClient) { }

  /**
   * Get line details for schedule selection
   */
  getLineDetails(
    baseUrl: string,
    mainScreenKey: string,
    leTaxType: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const actionCode = (leTaxType === 'PTSP' || leTaxType === 'BRPTP' || leTaxType === 'BRPTSP') ? 'r7pupz' : '6mnbxg';
    
    // Build params like AngularJS - merge globalParams and filterParams
    const params: any = {};
    if (globalService && globalService.globalParams) {
      Object.assign(params, globalService.globalParams);
    }
    Object.assign(params, filterParams);
    
    // Set specific parameters
    params.action_code = actionCode;
    params.screen_key = mainScreenKey;
    
    // Convert all params to URLSearchParams
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    const url = `${baseUrl}/loadJsonObject`;
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Get accounts for typeahead search
   */
  getAccounts(
    baseUrl: string,
    searchString: string,
    mainScreenKey: string,
    chartKey: number,
    parentAcctKey: number,
    acctTypeStr: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const urlEncoded = new URLSearchParams();
    
    // Add all parameters from globalService and filterParams
    const params: any = {};
    if (globalService && globalService.globalParams) {
      Object.assign(params, globalService.globalParams);
    }
    Object.assign(params, filterParams);
    
    // Set specific parameters
    params.action_code = 'vgrdg8';
    params.screen_key = mainScreenKey;
    params.search_string = searchString;
    params.chart_key = chartKey.toString();
    params.parent_acct_key = parentAcctKey.toString();
    params.acct_type = acctTypeStr;
    
    // Convert all params to URLSearchParams
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    const url = `${baseUrl}/loadJsonObject`;
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Get existing recurring EP adjustments
   */
  getExistingData(
    baseUrl: string,
    combinationKey: number,
    chartKey: number,
    type: string,
    epAutoKey: number | null,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    // ✅ CRITICAL: AngularJS uses JsonObjectFactory.getJSONObj which uses GET with query params
    // AND it merges globalParams and filterParams!
    const params: any = {};
    
    // Merge globalParams and filterParams first (like AngularJS JsonObjectFactory does)
    if (globalService && globalService.globalParams) {
      Object.assign(params, globalService.globalParams);
    }
    Object.assign(params, filterParams);
    
    // Then set specific parameters (these will override if they exist)
    params.action_code = 'lm9x8i';
    params.chart_key = chartKey.toString();
    params.type = type;
    params.combination_key = combinationKey.toString();
    
    // ✅ CRITICAL: ep_auto_key should be null or empty when loading all
    if (epAutoKey !== null && epAutoKey !== undefined) {
      params.ep_auto_key = epAutoKey.toString();
    }
    
    console.log('RecurringEP Service: getExistingData params:', params);
    console.log('🔍 FULL PAYLOAD TO BACKEND:', JSON.stringify(params, null, 2));
    
    // ✅ CRITICAL: AngularJS sends empty strings as empty, not omitted
    // Make sure we include ALL params from AngularJS request:
    // activity_key, is_locked, issue_id, issue_id_for_tx_save, issue_short_desc, 
    // jcd_key, jcd_ta_key, screen_key, screen_type
    
    // Check if we're missing any critical parameters
    const requiredParams = ['activity_key', 'is_locked', 'jcd_key', 'jcd_ta_key', 'screen_key', 'screen_type'];
    const missingParams = requiredParams.filter(p => !(p in params));
    if (missingParams.length > 0) {
      console.warn('⚠️ MISSING PARAMETERS that AngularJS sends:', missingParams);
      console.warn('   These might be required by backend! Check globalParams.');
    }
    
    // ✅ CRITICAL: Use GET with query params (like AngularJS JsonObjectFactory)
    const httpParams = new HttpParams({ fromObject: params });
    
    console.log('🌐 REQUEST URL:', `${baseUrl}/loadJsonObject?${httpParams.toString()}`);
    
    const url = `${baseUrl}/loadJsonObject`;
    return this.http.get<any>(url, { params: httpParams, withCredentials: true });
  }

  /**
   * Save recurring EP adjustments
   */
  saveAdjustments(
    baseUrl: string,
    headerData: any[],
    transData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const jsonSettings = JSON.stringify(headerData);
    const jsonObj = JSON.stringify(transData);
    
    // ✅ CRITICAL: Build payload EXACTLY like original AngularJS
    // Use _.merge() and _.extend() like in AngularJS service
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Add JSON data using _.extend() like AngularJS
    params = _.extend({ jsonObj: jsonObj }, params);
    params = _.extend({ jsonSettings: jsonSettings }, params);
    
    // ✅ CRITICAL: Add required configuration parameters with proper fallbacks
    // These are REQUIRED by backend and must be present
    params.tax_year = params.tax_year || filterParams?.tax_year || globalService?.globalParams?.tax_year || rowData?.TAX_YEAR || new Date().getFullYear();
    params.scenario = params.scenario || filterParams?.scenario || globalService?.globalParams?.scenario || rowData?.SCENARIO || 74;
    params.jcd_key = params.jcd_key || filterParams?.jcd_key || globalService?.globalParams?.jcd_key || rowData?.JCD_KEY || 250;
    
    // ✅ CRITICAL: scenarioCode - check multiple possible sources
    params.scenarioCode = params.scenarioCode || 
                         filterParams?.scenarioCode || 
                         filterParams?.scenario_code || 
                         globalService?.globalParams?.scenarioCode || 
                         globalService?.globalParams?.scenario_code || 
                         'RAF';
    
    // ✅ CRITICAL: is_issue_key_enabled - always required
    params.is_issue_key_enabled = params.is_issue_key_enabled || 
                                  filterParams?.is_issue_key_enabled || 
                                  globalService?.globalParams?.is_issue_key_enabled || 
                                  'N';
    
    console.log('recurring ep params to save:', params);
    
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    const url = `${baseUrl}/saveJsonObject?action_code=13dgei`;
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Delete recurring EP adjustments
   */
  deleteAdjustments(
    baseUrl: string,
    deleteData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const jsonObj = JSON.stringify(deleteData);
    
    // ✅ CRITICAL: Build payload EXACTLY like original AngularJS
    // Use _.merge() and _.extend() like in AngularJS service
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    
    // Add JSON data using _.extend() like AngularJS
    params = _.extend({ jsonObj: jsonObj }, params);
    
    // ✅ CRITICAL: Add required configuration parameters with proper fallbacks
    params.tax_year = params.tax_year || filterParams?.tax_year || globalService?.globalParams?.tax_year || rowData?.TAX_YEAR || new Date().getFullYear();
    params.scenario = params.scenario || filterParams?.scenario || globalService?.globalParams?.scenario || rowData?.SCENARIO || 74;
    params.jcd_key = params.jcd_key || filterParams?.jcd_key || globalService?.globalParams?.jcd_key || rowData?.JCD_KEY || 250;
    
    // ✅ CRITICAL: scenarioCode - check multiple possible sources
    params.scenarioCode = params.scenarioCode || 
                         filterParams?.scenarioCode || 
                         filterParams?.scenario_code || 
                         globalService?.globalParams?.scenarioCode || 
                         globalService?.globalParams?.scenario_code || 
                         'RAF';
    
    // ✅ CRITICAL: is_issue_key_enabled - always required
    params.is_issue_key_enabled = params.is_issue_key_enabled || 
                                  filterParams?.is_issue_key_enabled || 
                                  globalService?.globalParams?.is_issue_key_enabled || 
                                  'N';
    
    console.log('recurring ep params to delete:', params);
    
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    const url = `${baseUrl}/saveJsonObject?action_code=2e9aww`;
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}

